// Die Kiste als Datenmodell (task-014, docs/architektur-paket-2.md, Tabelle `chests`): sechzehn
// Fächer, jedes leer (`null`) oder ein Stapel. Ein Klick in der Kistenansicht verschiebt immer
// einen ganzen Stapel zwischen Leiste (Inventar) und Kiste; gleiche Arten stapeln sich.
//
// Hier steht nur die Regel, ohne Speichern und ohne Nachrichten. Der Server ruft sie auf, nachdem
// er Besitz, Nähe und die geöffnete Kiste geprüft hat, und bucht das Ergebnis in einer
// Transaktion (ADR 0031). Die Funktion verändert nichts, was sie bekommt: Scheitert das Buchen,
// gilt einfach der alte Stand weiter.

import type { ChestSide } from '../protocol/messages.js';
import type { ItemStack } from './inventory.js';

/** Fächer einer Kiste. */
export const KISTE_FAECHER = 16;

/**
 * Fächer der Leiste am Bildrand. Mehr verschiedene Arten nimmt man aus der Kiste nicht mit: Ein
 * neunter Stapel stünde in keinem Fach und wäre unsichtbar.
 */
export const LEISTE_FAECHER = 8;

/** Ein Fach der Kiste: leer oder ein Stapel. */
export type Kistenfach = ItemStack | null;

/** Eine leere Kiste; jeder Aufruf liefert eine eigene Liste. */
export function leereKiste(): Kistenfach[] {
  return Array.from({ length: KISTE_FAECHER }, () => null);
}

/** Warum eine Verschiebung nicht geht: Da liegt nichts, oder auf der anderen Seite ist kein Platz. */
export type VerschiebeGrund = 'leer' | 'kisteVoll' | 'leisteVoll';

/** Das Ergebnis: die neuen Listen beider Seiten, oder der Grund, warum alles bleibt. */
export type Verschiebung = { ok: true; stacks: ItemStack[]; slots: Kistenfach[] } | { ok: false; grund: VerschiebeGrund };

/**
 * Verschiebt den ganzen Stapel am Platz `index` von `from` auf die andere Seite. In der Kiste
 * landet er auf dem Fach mit derselben Art, sonst im ersten freien; in der Leiste auf dem Stapel
 * derselben Art, sonst hinten. Ein leeres Fach, ein Platz, den es nicht gibt (negativ, krumm,
 * hinter dem letzten Stapel, außerhalb der sechzehn Fächer), ist `leer`; fehlt drüben der Platz
 * für eine neue Art, `kisteVoll` bzw. `leisteVoll`. Die übergebenen Listen bleiben unverändert.
 */
export function verschiebeStapel(
  stacks: readonly ItemStack[],
  slots: readonly Kistenfach[],
  from: ChestSide,
  index: number,
): Verschiebung {
  if (!Number.isInteger(index) || index < 0) return { ok: false, grund: 'leer' };
  const neueStacks = stacks.map((stapel) => ({ ...stapel }));
  const neueSlots = slots.map((fach) => (fach === null ? null : { ...fach }));

  if (from === 'leiste') {
    const stapel = neueStacks[index];
    if (stapel === undefined) return { ok: false, grund: 'leer' };
    const gleich = neueSlots.find((fach, i) => i < KISTE_FAECHER && fach !== null && fach.item === stapel.item);
    if (gleich !== undefined && gleich !== null) {
      gleich.count += stapel.count;
    } else {
      const frei = neueSlots.findIndex((fach, i) => i < KISTE_FAECHER && fach === null);
      if (frei === -1) return { ok: false, grund: 'kisteVoll' };
      neueSlots[frei] = stapel;
    }
    neueStacks.splice(index, 1);
    return { ok: true, stacks: neueStacks, slots: neueSlots };
  }

  const stapel = index < KISTE_FAECHER ? neueSlots[index] : undefined;
  if (stapel === undefined || stapel === null) return { ok: false, grund: 'leer' };
  const gleich = neueStacks.find((vorhanden) => vorhanden.item === stapel.item);
  if (gleich !== undefined) {
    gleich.count += stapel.count;
  } else {
    if (neueStacks.length >= LEISTE_FAECHER) return { ok: false, grund: 'leisteVoll' };
    neueStacks.push(stapel);
  }
  neueSlots[index] = null;
  return { ok: true, stacks: neueStacks, slots: neueSlots };
}
